import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Users, Eye, Award, ArrowLeft } from 'lucide-react';

const UserPerformanceAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' ? { filter, ...customDates } : { filter };
      const response = await analyticsService.getUserPerformance(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching user performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const UserCard = ({ user, rank }) => (
    <div className="bg-white rounded-card shadow-card p-4 lg:p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          {rank <= 3 && (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
              rank === 1 ? 'bg-yellow-400' : rank === 2 ? 'bg-gray-300' : 'bg-blue-400'
            }`}>
              <Award className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-800">{user.name}</h3>
            <p className="text-sm text-gray-600">{user.city}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/users/view/${user.user_id}`)}
          className="p-2 text-brand hover:bg-brand-light rounded-full transition-colors"
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-600">Shipments</p>
          <p className="text-lg font-bold text-gray-800">{user.performance.shipment_count}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Revenue</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(user.performance.total_revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Collected</p>
          <p className="text-lg font-bold text-emerald-600">{formatCurrency(user.performance.collected)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Collection Rate</p>
          <p className="text-lg font-bold text-brand">{user.performance.collection_rate}%</p>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs text-gray-600 mb-2">Shipment Status</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(user.shipment_status_breakdown).map(([status, count]) => (
            <span key={status} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
              {status}: {count}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout title="User Performance Analytics" icon={Users}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading user performance...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="User Performance Analytics"
      icon={Users}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Agent and employee metrics</p>

                {/* Top Performers */}
                {data?.top_performers && data.top_performers.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <Award className="w-6 h-6 text-yellow-500 mr-2" />
                      Top Performers
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {data.top_performers.slice(0, 3).map((user, index) => (
                        <UserCard key={user.user_id} user={user} rank={index + 1} />
                      ))}
                    </div>
                  </div>
                )}

                {/* All Users */}
                {data?.users && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">All Users Performance</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {data.users.map((user, index) => (
                        <UserCard key={user.user_id} user={user} rank={index + 1} />
                      ))}
                    </div>
                  </div>
                )}

                {data?.users && data.users.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-card">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No user performance data available for the selected period</p>
                  </div>
                )}
    </PageLayout>
  );
};

export default UserPerformanceAnalytics;